import { useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { sortByOrder } from 'utils/collection-manipulation/array-sort-by';
import {
  advancedEventsLayoutRelations,
  events,
  markets
} from 'utils/constants/sportsbook/data-fields';
import { lazy } from 'utils/generic/lazy';
import { createArray } from 'utils/helpers/create-fake-array';
import { isMobile } from 'utils/is-mobile';
import RidGenerator from 'utils/swarm/rid-generator';
import {
  TAdvancedSelectedMarkets,
  TGameMainInfoType,
  TGameMarketsInfoType
} from 'interfaces/sportsbook';
import { Game } from 'interfaces/sportsbook-data-levels';
import { executeCommand, unsubscribeCommand } from 'services/get-data-level';
import { TPartialGameWithInfoType } from 'newelements/Sportsbook/layouts/advanced/__partials/AdvancedMainContent/__partials/AdvancedGamesByCompetitions/FilteredByTimeWrapper';
import { ADVANCED_SKELETON_COUNTS } from 'newelements/Sportsbook/layouts/advanced/constants/skeleton-counts';
import { AdvancedMatchCardSkeleton } from 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedMatchCard/Skeleton';
import { useGetMarketsCounts } from 'newelements/Sportsbook/layouts/advanced/hooks/useGetMarketsCounts';
import { MatchCardInlineSkeleton } from 'components/MatchCardInline/Skeleton';
import { useMounted } from 'hooks/useMounted';
import {
  setAdvancedGamesMainInfo,
  setAdvancedGamesMarketsInfo
} from 'store/actions/new-sportsbook';
import {
  getAdvancedGamesMainInfo,
  getAdvancedGamesMarketsInfo
} from 'store/selectors/new-sportsbook';
import { getStreamData } from 'store/selectors/sport-data';

const { MobileMatchCardWrapper } = lazy(
  () =>
    import(
      /* webpackChunkName: "advanced-match-card-mobile" */ './MobileMatchCardWrapper'
    )
);

const { AdvancedMatchCard } = lazy(
  () =>
    import(
      /* webpackChunkName: "advanced-match-card-desktop" */ 'newelements/Sportsbook/layouts/advanced/Desktop/__partials/AdvancedMatchCard'
    )
);

const DEFAULT_MARKETS = {
  first: 'MatchWinner',
  second: 'P1P2',
  third: 'MatchHandicap2'
};

const MARKETS_BY_SPORTS: Record<string, TAdvancedSelectedMarkets> = {
  Soccer: {
    first: 'P1XP2',
    second: '1X12X2',
    third: 'DrawNoBet'
  },
  Basketball: {
    first: 'P1P2',
    second: 'MatchHandicap',
    third: 'MatchTotal'
  },
  Tennis: {
    first: 'P1P2',
    second: 'Handicap',
    third: 'TotalGamesOver/Under'
  },
  Volleyball: {
    first: 'P1P2',
    second: 'PointHandicap',
    third: 'TotalPointsOver/Under'
  },
  IceHockey: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'MatchHandicap2'
  },
  TableTennis: {
    first: 'P1P2',
    second: 'MatchPointHandicap',
    third: 'MatchTotalPointsOverUnder'
  },
  Handball: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'MatchHandicap2'
  },
  Cricket: {
    first: 'P1P2',
    second: 'P1XP2',
    third: '1X12X2'
  },
  Archery: {
    first: 'MatchPointsTotal2Way',
    second: 'MatchPointsEvenOdd',
    third: 'RoundPointsTotal2Way'
  },
  AmericanFootball: {
    first: 'P1P2',
    second: 'MatchHandicap',
    third: 'MatchTotal'
  },
  AustralianFootball: {
    first: 'P1P2',
    second: 'Handicap',
    third: 'TotalPointsOver/Under'
  },
  PistolShooting: {
    first: 'MatchPointCommonTotalOverUnder2Way',
    second: 'RoundPointCommonTotalOverUnder2Way',
    third: 'MatchPointEvenOdd'
  },
  Baseball: {
    first: 'P1P2',
    second: 'RunLine',
    third: 'TotalRunsOver/Under'
  },
  Darts: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'HandicapLegs'
  },
  Floorball: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'Handicap'
  },
  Boxing: {
    first: 'P1P2',
    second: 'TotalRounds',
    third: 'WillFightGoTheDistance'
  },
  BallHockey: {
    first: 'P1XP2',
    second: 'P1XP2',
    third: '1X12X2'
  },
  GaelicFootball: {
    first: 'MatchWinner',
    second: 'MatchResult',
    third: 'MatchHandicap'
  },
  Futsal: {
    first: 'P1XP2',
    second: 'Handicap',
    third: 'TotalGoalsOver/Under'
  },
  Mma: {
    first: 'P1P2',
    second: 'MatchResult',
    third: 'TotalRounds'
  },
  Hurling: {
    first: 'MatchWinner',
    second: 'P1XP2',
    third: 'MatchHandicap'
  },
  RugbyLeague: {
    first: 'MatchWinner',
    second: 'P1XP2',
    third: 'Handicap(NoDraw)'
  },
  Chess: {
    first: 'P1P2',
    second: 'MatchResult',
    third: '1X12X2'
  },
  Badminton: {
    first: 'P1P2',
    second: 'MatchPointsHandicap',
    third: 'GamesHandicap'
  },
  Rowing: {
    first: 'MatchResult'
  },
  Snooker: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'Handicap'
  },
  CyberFootball: {
    first: 'P1XP2',
    second: '1X12X2',
    third: 'Handicap'
  },
  X1Football: {
    first: 'MatchWinner'
  },
  Speedway: {
    first: 'MatchWinner',
    second: 'P1XP2',
    third: 'AsianHandicap'
  },
  Halo: {
    first: 'MatchWinner',
    second: 'MapsHandicap',
    third: 'MapsTotal'
  },
  Softball: {
    first: 'P1P2',
    second: 'RunLine',
    third: 'TotalRunsOver/Under'
  },
  RugbyUnion: {
    first: 'P1XP2',
    second: 'Handicap(NoDraw)',
    third: 'TotalPointsOver/Under'
  },
  Lacross: {
    first: 'MatchResult',
    second: 'Handicap',
    third: 'MatchTotal'
  },
  Squash: {
    first: 'MatchResult',
    second: 'PointsHandicap',
    third: 'GamesHandicap'
  },
  WaterPolo: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'Handicap'
  },
  CounterStrike: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'MapsHandicap'
  },
  Dota2: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'GamesHandicap'
  },
  CallOfDuty: {
    first: 'P1P2',
    second: 'GamesHandicap',
    third: 'GamesTotal'
  },
  KingOfGlory: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'GamesHandicap'
  },
  LeagueOfLegends: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'GamesHandicap'
  },
  StarCraft2: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'GamesHandicap'
  },
  CrossFire: {
    first: 'MatchWinner',
    second: 'MatchResult',
    third: 'MapsHandicap'
  },
  MobileLegends: {
    first: 'P1P2',
    second: 'P1XP2',
    third: 'GamesHandicap'
  },
  EBasketball: {
    first: 'P1P2',
    second: 'MatchHandicap',
    third: 'MatchTotal'
  },
  WarcraftIII: {
    first: 'P1P2',
    second: 'GamesHandicap'
  }
};

type TGamesListFilteredByTimeProps = {
  gamesArr: TPartialGameWithInfoType[];
  selectedEventIds: string[];
  reduxKey: string;
};

const GAMES_BY_TIME_MARKETS_COUNTS_KEY = 'gamesByTimeKey';

export const GamesListFilteredByTime = ({
  gamesArr,
  selectedEventIds,
  reduxKey
}: TGamesListFilteredByTimeProps) => {
  const { mounted } = useMounted();
  const dispatch = useDispatch();

  const streamData = useSelector(getStreamData);

  const gamesMainData = useSelector(getAdvancedGamesMainInfo)?.[reduxKey];

  const gamesMarketsData = useSelector(getAdvancedGamesMarketsInfo)?.[reduxKey];

  const { gameIds, sportAliases } = useMemo(() => {
    return gamesArr.reduce(
      (acc: { gameIds: number[]; sportAliases: string[] }, game) => {
        return {
          gameIds: [...acc.gameIds, game.id],
          sportAliases: [...acc.sportAliases, game.sport_alias || '']
        };
      },
      { gameIds: [], sportAliases: [] }
    );
  }, [gamesArr]);

  const { marketsCounts } = useGetMarketsCounts({
    gameIds,
    reduxKey: GAMES_BY_TIME_MARKETS_COUNTS_KEY + reduxKey
  });

  const { gamesGeneralInfo, gamesMarketsInfo } = useMemo(() => {
    if (gamesArr.length && gamesMainData && gamesMarketsData) {
      const _gamesGeneralInfo: Record<string, TGameMainInfoType> = {};
      const _gamesMarketsInfo: Record<string, TGameMarketsInfoType> = {};

      gamesArr.forEach(game => {
        const gameInfoInResponse = gamesMainData[game.id];

        if (gameInfoInResponse) {
          const gameGeneralInfo: TGameMainInfoType = {
            ...game,
            team1_id: gameInfoInResponse.team1_id,
            team2_id: gameInfoInResponse.team2_id,
            team1_name: gameInfoInResponse.team1_name,
            team2_name: gameInfoInResponse.team2_name,
            info: gameInfoInResponse.info,
            stats: gameInfoInResponse.stats,
            is_blocked: gameInfoInResponse.is_blocked,
            tv_type: gameInfoInResponse.tv_type,
            video_id: gameInfoInResponse.video_id,
            video_id2: gameInfoInResponse.video_id2,
            is_feed_available: gameInfoInResponse.is_feed_available,
            text_info: gameInfoInResponse.text_info,
            is_live: gameInfoInResponse.is_live,
            is_stat_available: gameInfoInResponse.is_stat_available,
            sportcast_id: gameInfoInResponse.sportcast_id,
            show_type: gameInfoInResponse.show_type
          };

          _gamesGeneralInfo[game.id] = gameGeneralInfo;
          _gamesMarketsInfo[game.id] = gamesMarketsData[game.id]?.market;
        }
      });

      return {
        gamesGeneralInfo: _gamesGeneralInfo,
        gamesMarketsInfo: _gamesMarketsInfo
      };
    }

    return {};
  }, [gamesArr, gamesMainData, gamesMarketsData]);

  useLayoutEffect(() => {
    if (gameIds.length) {
      const marketTypesBySportAliases = new Set(
        sportAliases
          .map(sport =>
            Object.values(MARKETS_BY_SPORTS[sport] || DEFAULT_MARKETS)
          )
          .flat()
      );

      const rIdForGameInfo = RidGenerator.gForSubscribe(
        `ADVANCED_GET_GAMES_MAIN_INFO_BY_TIME`
      );

      const whatForGameInfo = {
        game: advancedEventsLayoutRelations.game
      };

      const whereForGameInfo = {
        game: { id: { '@in': gameIds } }
      };

      const onResponseForGameInfo = (data: { game: Record<string, Game> }) => {
        if (data && data.game && mounted.current) {
          dispatch(
            setAdvancedGamesMainInfo({
              data: data.game,
              key: reduxKey
            })
          );
        }
      };

      executeCommand(
        rIdForGameInfo,
        whatForGameInfo,
        whereForGameInfo,
        onResponseForGameInfo,
        true,
        onResponseForGameInfo
      );

      const rIdForMarkets = RidGenerator.gForSubscribe(
        `ADVANCED_GET_GAMES_MARKETS_BY_TIME`
      );

      const whatForMarkets = {
        game: [['id']],
        market: markets.market,
        event: events.event
      };

      const whereForMarkets = {
        game: { id: { '@in': gameIds } },
        market: {
          type: { '@in': Array.from(marketTypesBySportAliases) }
        }
      };

      const onResponseForMarkets = (data: {
        game: Record<string, { id: number; market: TGameMarketsInfoType }>;
      }) => {
        if (data && data.game && mounted.current) {
          dispatch(
            setAdvancedGamesMarketsInfo({
              data: data.game,
              key: reduxKey
            })
          );
        }
      };

      executeCommand(
        rIdForMarkets,
        whatForMarkets,
        whereForMarkets,
        onResponseForMarkets,
        true,
        onResponseForMarkets
      );

      return () => {
        unsubscribeCommand(rIdForGameInfo);
        unsubscribeCommand(rIdForMarkets);
      };
    }
  }, [gameIds.toString()]);

  const isDataReadyToShow =
    gamesArr &&
    gamesGeneralInfo &&
    Object.keys(gamesGeneralInfo).length &&
    gamesMarketsInfo &&
    marketsCounts;

  return isDataReadyToShow
    ? sortByOrder(gamesArr, 'start_ts').map(({ id, sport_alias }) => {
        const gameGeneralInfo = gamesGeneralInfo[id];
        const gameMarketsInfo = gamesMarketsInfo[id];

        if (gameGeneralInfo) {
          return isMobile() ? (
            <MobileMatchCardWrapper
              key={id}
              gameGeneralInfo={gameGeneralInfo}
              gameMarketsInfo={{ market: gameMarketsInfo }}
              marketsCount={marketsCounts[id]}
            />
          ) : (
            <AdvancedMatchCard
              key={id}
              gameGeneralInfo={gameGeneralInfo}
              gameMarketsInfo={gameMarketsInfo}
              selectedMarkets={
                MARKETS_BY_SPORTS[sport_alias || ''] || DEFAULT_MARKETS
              }
              selectedEventIds={selectedEventIds}
              isLiveStreamingAvailable={!!streamData?.[id]}
              showSportName
            />
          );
        }
      })
    : createArray(ADVANCED_SKELETON_COUNTS._5).map(key =>
        isMobile() ? (
          <MatchCardInlineSkeleton key={key} />
        ) : (
          <AdvancedMatchCardSkeleton key={key} />
        )
      );
};
